/**
 * Ephemeral / “done” journey surfaces — never restore via Zpět visit stack or
 * browser POP history. Payment monitoring / QR / complete can 404 or error once
 * the payment session is gone; skip them and land on a safe parent instead.
 */

import { tenantRelativeMorePath } from '../features/more/moreRouteUtils.js';
import { isPlatformChromeTenant } from '../app/platformChromeTenant.js';
import { resolvePostAuthFallbackPath } from './navigationEngine.js';

function normalizePathname(pathname: string): string {
  const noHash = pathname.split('#', 1)[0] ?? pathname;
  const noQuery = noHash.split('?', 1)[0] ?? noHash;
  const trimmed = noQuery.trim();
  if (trimmed.length <= 1) {
    return trimmed === '' ? '/' : trimmed;
  }
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

/**
 * True for payment-monitoring / terminal / one-shot surfaces that must not be
 * re-entered via history after the user has left them.
 */
export function isEphemeralBackRestorePath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  const rel = tenantRelativeMorePath(path);

  if (rel === '/checkout' || rel.startsWith('/checkout/')) {
    return true;
  }
  if (rel === '/handoff' || rel.startsWith('/handoff/')) {
    return true;
  }
  if (rel === '/post-kiosk' || rel.startsWith('/post-kiosk')) {
    return true;
  }
  if (rel.startsWith('/account/payments/claim')) {
    return true;
  }
  if (/^\/account\/payments\/[^/]+\/qr(?:\/|$)/.test(rel)) {
    return true;
  }
  // Interrupted-journey recovery — returnTo may point at a stale payment URL.
  if (rel === '/recovery' || rel.startsWith('/recovery/')) {
    return true;
  }
  return false;
}

function tenantFromPath(pathname: string): string | null {
  const path = normalizePathname(pathname);
  const segments = path.split('/').filter((s) => s.length > 0);
  const first = segments[0];
  if (first === undefined || first.length === 0) {
    return null;
  }
  return first;
}

/**
 * Safe landing when history/stack would restore an ephemeral payment surface.
 * Account payment QR → payments list; everything else → shop (or `/` on platform).
 */
export function resolveEphemeralBackFallback(
  ephemeralPathname: string,
  tenantCode: string,
): string {
  const path = normalizePathname(ephemeralPathname);
  const rel = tenantRelativeMorePath(path);
  const pathTenant = tenantFromPath(path);
  const effectiveTenant =
    tenantCode.trim().length > 0 && !isPlatformChromeTenant(tenantCode)
      ? tenantCode
      : (pathTenant ?? tenantCode);

  // Wave D — payment QR ephemeral fallback is platform `/account/orders`.
  if (/^\/account\/payments\/[^/]+\/qr(?:\/|$)/.test(rel)) {
    return '/account/orders';
  }

  if (
    effectiveTenant.trim().length === 0 ||
    isPlatformChromeTenant(effectiveTenant)
  ) {
    return '/';
  }
  return resolvePostAuthFallbackPath(effectiveTenant);
}
